import { saveWebCookieState } from '@egr/xbox/app-api/AppAction';
import { isReactNativeApp } from '@egr/xbox/app-api/AppDetection';
import { appInfo } from '@egr/xbox/app-api/AppInfo';
import { enableTracking, disableTracking, isMatomoActive } from '@egr/xbox/telemetry/MatomoUtils';
import { showDebugFlag } from '@egr/xbox/utils/Debug';
import { getRandomHexString } from '@egr/xbox/utils/Helper';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';
import { enableSentry } from '@egr/xbox/utils/errors/Error';
import { PersistentStore } from '@egr/xbox/utils/store/PersistentStore';

import { isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

interface CookieUserSetting {
    submitDialog?: () => void;
    dialogSubmitted: boolean;
    /** Sentry */
    diagnostics: boolean;
    /** Matomo */
    telemetry: boolean;
    youtube: boolean;
    /** Only for matomo in mobile app */
    visitorId?: string;

    tempDiagnostics: boolean;
    tempTelemetry: boolean;
    tempYoutube: boolean;
}

const DISABLE_WEB_COOKIE_DIALOG = getEnvBoolean('DISABLE_WEB_COOKIE_DIALOG', false);

class CookieDialogStore extends PersistentStore<CookieUserSetting> {
    constructor() {
        super(
            'cookieDialog',
            {
                submitDialog: undefined,
                dialogSubmitted: DISABLE_WEB_COOKIE_DIALOG,
                telemetry: false,
                youtube: false,
                diagnostics: false,

                tempTelemetry: false,
                tempYoutube: false,
                tempDiagnostics: false,
            },
            ['telemetry', 'diagnostics', 'youtube', 'dialogSubmitted'],
        );
        this.setDialogState = this.setDialogState.bind(this);
        this.updateMatomo = this.updateMatomo.bind(this);
        if (!DISABLE_WEB_COOKIE_DIALOG) {
            this.updateSentry();
            this.updateMatomo();
            this.subscribe(() => {
                if (this.snapshot.dialogSubmitted) {
                    this.updateSentry();
                    this.updateMatomo();
                }
            });
        }
    }

    public async waitForSubmit(forceShow: boolean = false): Promise<void> {
        if (this.snapshot.dialogSubmitted && !forceShow) {
            return;
        }
        return new Promise<void>((submitDialog) => {
            const { telemetry, diagnostics, youtube } = this.snapshot;
            this.updateState({
                tempDiagnostics: diagnostics,
                tempTelemetry: telemetry,
                tempYoutube: youtube,
                dialogSubmitted: false,
                submitDialog
            });
        });
    }

    public updateMatomo(): void {
        const { dialogSubmitted, telemetry, visitorId } = this.snapshot;

        const trackingShouldBeEnabled: boolean = (telemetry && dialogSubmitted) || (appInfo != null);

        if (trackingShouldBeEnabled) {
            if (!isMatomoActive()) {
                if (appInfo == null) {
                    void enableTracking();
                } else {
                    let localVisitorId = visitorId;
                    if (isNullOrEmpty(localVisitorId)) {
                        localVisitorId = getRandomHexString(16);
                        this.updateState({ visitorId: localVisitorId });
                    }
                    if (getEnvBoolean('DEBUG_MATOMO_VISITORID', false)) {
                        showDebugFlag({
                            key: 'matomo-visitorid',
                            value: 'matomo-visitorid:' + localVisitorId
                        });
                    }
                    void enableTracking(localVisitorId);
                }
            }
        } else {
            void disableTracking();
        }
    }

    protected setDialogState(): void {
        const { diagnostics, youtube } = this.snapshot;
        cookieDialogStore.updateState({
            youtube,
            diagnostics,
            telemetry: isMatomoActive(),
        });
    }

    protected updateSentry(): void {
        const { diagnostics } = this.snapshot;
        if (diagnostics == null || appInfo != null) {
            return;
        }
        enableSentry(diagnostics);
    }

    protected async submitDialog(state: { youtube?: boolean; telemetry?: boolean; diagnostics?: boolean; }): Promise<void> {
        if (isReactNativeApp) {
            saveWebCookieState({
                youtube: state.youtube ?? this.snapshot.youtube,
                telemetry: state.telemetry ?? this.snapshot.telemetry,
                diagnostics: state.diagnostics ?? this.snapshot.youtube,
                hide: true,
            });
        }
        return this.updateState({
            ...state,
            dialogSubmitted: true,
        });
    }
}

export const cookieDialogStore = new CookieDialogStore();
